import { useEffect, useRef, useState } from 'react';

/**
 * Космический фон входа: три слоя глубины в цветах системы.
 *
 * Слои независимы, поэтому картинка не повторяется:
 *  — межзвёздная дымка и клубы туманности живут на CSS (браузер считает их
 *    сам, без участия скрипта);
 *  — звёздное поле рисуется на canvas: разная величина, температура и ритм
 *    мерцания, ближние крупнее и идут быстрее дальних;
 *  — курсор разводит слои с разной скоростью — так появляется объём.
 *
 * Ни одной библиотеки и ни одной картинки: страница входа обязана открываться
 * мгновенно даже на плохой связи, это первое, что видит человек.
 *
 * Уважает системную настройку «меньше движения»: там рисуется один
 * неподвижный кадр вместо анимации.
 */

type Star = {
  x: number;
  y: number;
  r: number;
  a: number;
  sp: number;
  layer: number;
  tint: string;
  phase: number;
  twinkle: number;
};

type Meteor = { x: number; y: number; vx: number; vy: number; len: number; life: number; max: number };

/** Доля звёзд, скорость, радиус и яркость по слоям — от дальних к ближним. */
const LAYERS = [
  { share: 0.48, speed: 0.012, radius: 0.9, alpha: 0.6 },
  { share: 0.32, speed: 0.03, radius: 1.6, alpha: 0.82 },
  { share: 0.2, speed: 0.06, radius: 2.5, alpha: 1 },
];

const TINTS = ['255,255,255', '200,222,255', '168,196,255', '196,178,255'];

export function CosmicBackground() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);

  /*
   * Ширина снимается ОДИН раз при первой отрисовке: поворот экрана и так
   * перезагрузит страницу входа, а слежение за шириной вернуло бы сюда
   * пересборку холста — ровно то, от чего уходим.
   */
  const [uzkiyEkranSNachala] = useState(
    () => window.matchMedia?.('(max-width: 760px)').matches ?? false,
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    /*
     * ⛔⛔ НА ТЕЛЕФОНЕ — ОДИН НЕПОДВИЖНЫЙ КАДР (`s605`, владелец: «ксиоми как
     * паралитик. фон подмаргивает… на входе я даже войти не могу» — БЛОКЕР).
     *
     * Непрерывная перерисовка холста во весь экран дорога везде, но на
     * телефоне она встречается со слабым отрисовщиком и с панелью браузера,
     * которая ездит при каждом касании. На Mi Browser это выглядит как
     * подмаргивающий фон — и человек не может войти в систему.
     *
     * Звёздное небо — украшение первой страницы. Оно не стоит того, чтобы
     * из-за него нельзя было работать: на узком экране рисуем ОДИН кадр тем
     * же кодом, что и для системной настройки «меньше движения».
     */
    const uzkiyEkran = window.matchMedia?.('(max-width: 760px)').matches ?? false;
    const bezDvizheniya = reduceMotion || uzkiyEkran;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let stars: Star[] = [];
    let meteors: Meteor[] = [];
    let tick = 0;
    let raf: number | null = null;

    // Сглаженное следование за курсором: резкий сдвиг читался бы как рывок.
    let mx = 0;
    let my = 0;
    let targetX = 0;
    let targetY = 0;

    const build = () => {
      const area = (width * height) / (dpr * dpr);
      // Плотность привязана к площади: на телефоне звёзд втрое меньше, чем
      // на мониторе, — иначе мелкий экран превращается в кашу, а слабое
      // устройство зря греется.
      const total = Math.max(120, Math.min(520, Math.round(area / 1750)));

      stars = [];
      LAYERS.forEach((layer, index) => {
        const count = Math.round(total * layer.share);
        for (let i = 0; i < count; i += 1) {
          stars.push({
            x: Math.random() * width,
            y: Math.random() * height,
            r: (0.35 + Math.random() * layer.radius) * dpr,
            a: layer.alpha * (0.35 + Math.random() * 0.65),
            sp: layer.speed * dpr * (0.6 + Math.random() * 0.8),
            layer: index,
            tint: TINTS[Math.floor(Math.random() * TINTS.length)],
            phase: Math.random() * Math.PI * 2,
            twinkle: 0.6 + Math.random() * 1.9,
          });
        }
      });
    };

    /*
     * ⛔⛔ КЛАВИАТУРА — НЕ ПОВОРОТ ЭКРАНА, И ПЕРЕСОБИРАТЬ ХОЛСТ ПО НЕЙ НЕЛЬЗЯ
     * (`s605`, владелец: «появляется белый квадрат, потом сам исчезает. когда
     * начинаешь вводить емаил опять появляется»).
     *
     * Присвоение `canvas.width` ОЧИЩАЕТ холст — это правило самого холста, а
     * не наша небрежность. На Android появление клавиатуры меняет высоту окна
     * и поднимает `resize`; холст обнулялся, звёзды пересобирались заново, и
     * на кадр экран становился пустым. Ровно это владелец и описал: белый
     * квадрат, который появляется при вводе и исчезает сам.
     *
     * Настоящий повод пересобрать — изменение ШИРИНЫ: поворот экрана или окно
     * на столе. Высота меняется от панели браузера и клавиатуры по десять раз
     * за минуту, и к содержимому это отношения не имеет.
     */
    let byloShiriny = -1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const shirina = Math.round(rect.width);

      if (shirina === byloShiriny) return;

      byloShiriny = shirina;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = Math.max(1, Math.round(rect.width * dpr));
      height = Math.max(1, Math.round(rect.height * dpr));
      canvas.width = width;
      canvas.height = height;
      build();
    };

    const spawnMeteor = () => {
      const fromTop = Math.random() < 0.6;
      meteors.push({
        x: fromTop ? Math.random() * width * 0.9 : -60 * dpr,
        y: fromTop ? -40 * dpr : Math.random() * height * 0.5,
        vx: (2.2 + Math.random() * 1.8) * dpr,
        vy: (1.1 + Math.random() * 1) * dpr,
        len: (90 + Math.random() * 120) * dpr,
        life: 0,
        max: 90 + Math.random() * 50,
      });
    };

    const drawStaticFrame = () => {
      ctx.clearRect(0, 0, width, height);
      stars.forEach((s) => {
        ctx.fillStyle = `rgba(${s.tint},${s.a.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      mx += (targetX - mx) * 0.045;
      my += (targetY - my) * 0.045;

      stars.forEach((s) => {
        s.y += s.sp;
        s.x += s.sp * 0.35;
        if (s.y > height + 4) {
          s.y = -4;
          s.x = Math.random() * width;
        }
        if (s.x > width + 4) s.x = -4;

        const px = s.x + mx * (s.layer + 1) * 6 * dpr;
        const py = s.y + my * (s.layer + 1) * 6 * dpr;
        const tw = 0.55 + 0.45 * Math.sin(tick * 0.02 * s.twinkle + s.phase);
        const alpha = s.a * tw;

        // Ближние звёзды получают ореол — без него поле выглядит плоской сыпью.
        if (s.layer === 2) {
          const g = ctx.createRadialGradient(px, py, 0, px, py, s.r * 4.5);
          g.addColorStop(0, `rgba(${s.tint},${(alpha * 0.5).toFixed(3)})`);
          g.addColorStop(1, `rgba(${s.tint},0)`);
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(px, py, s.r * 4.5, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.fillStyle = `rgba(${s.tint},${alpha.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(px, py, s.r, 0, Math.PI * 2);
        ctx.fill();
      });

      meteors = meteors.filter((m) => {
        m.x += m.vx;
        m.y += m.vy;
        m.life += 1;
        const k = 1 - m.life / m.max;
        if (k <= 0 || m.x > width + 120 * dpr || m.y > height + 120 * dpr) return false;

        const d = Math.hypot(m.vx, m.vy) || 1;
        const tailX = m.x - (m.vx / d) * m.len;
        const tailY = m.y - (m.vy / d) * m.len;
        const grad = ctx.createLinearGradient(m.x, m.y, tailX, tailY);
        grad.addColorStop(0, `rgba(210,230,255,${(0.85 * k).toFixed(3)})`);
        grad.addColorStop(1, 'rgba(160,190,255,0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.6 * dpr;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(m.x - m.vx, m.y - m.vy);
        ctx.lineTo(tailX, tailY);
        ctx.stroke();
        return true;
      });

      if (Math.random() < 0.0022 && meteors.length < 2) spawnMeteor();

      tick += 1;
      raf = requestAnimationFrame(draw);
    };

    const onMove = (e: MouseEvent) => {
      const rect = host.getBoundingClientRect();
      targetX = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      targetY = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };

    const onVisibility = () => {
      // Спрятанная вкладка не должна жечь батарею.
      if (document.hidden) {
        if (raf) cancelAnimationFrame(raf);
        raf = null;
      } else if (!raf && !bezDvizheniya) {
        raf = requestAnimationFrame(draw);
      }
    };

    resize();
    window.addEventListener('resize', resize, { passive: true });

    if (bezDvizheniya) {
      drawStaticFrame();
    } else {
      host.addEventListener('mousemove', onMove, { passive: true });
      document.addEventListener('visibilitychange', onVisibility);
      raf = requestAnimationFrame(draw);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      host.removeEventListener('mousemove', onMove);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <div ref={hostRef} className="ops-cosmos" aria-hidden="true">
      <div className="ops-cosmos__nebula" />
      <i className="ops-cosmos__blob ops-cosmos__blob--5" />
      <i className="ops-cosmos__blob ops-cosmos__blob--1" />
      <i className="ops-cosmos__blob ops-cosmos__blob--2" />
      <i className="ops-cosmos__blob ops-cosmos__blob--3" />
      <i className="ops-cosmos__blob ops-cosmos__blob--4" />
      <div className="ops-cosmos__milky" />
      <div className="ops-cosmos__dust" />
      <div className="ops-cosmos__vignette" />
      {/*
        ⛔ ХОЛСТА НА ТЕЛЕФОНЕ НЕТ ВОВСЕ (`s605`, блокер). Даже один неподвижный
        кадр — это полноэкранный холст с учётом плотности точек: память и
        время у старого телефона. Требование владельца дословно: «должен
        работать у менеджеров с любого телефона даже самого старого».
        Тёмно-синий фон и градиенты дают ту же картинку без единой отрисовки.
      */}
      {!uzkiyEkranSNachala && <canvas ref={canvasRef} className="ops-cosmos__stars" />}
      <div className="ops-cosmos__halo" />
      <div className="ops-cosmos__veil" />
      <div className="ops-cosmos__grain" />
    </div>
  );
}
